import { getRolePath, Toaster } from "./common";

export const CLIENT_DETAILS_STORAGE_KEY = "finlo_open_client_details_id";
export const CLIENT_DETAILS_HEARTBEAT_KEY = "finlo_open_client_details_heartbeat";
export const CLIENT_DETAILS_TAB_KEY = "finlo_open_client_details_tab";

const STORAGE_KEY = CLIENT_DETAILS_STORAGE_KEY;
const HEARTBEAT_KEY = CLIENT_DETAILS_HEARTBEAT_KEY;
const TAB_KEY = CLIENT_DETAILS_TAB_KEY;
const STALE_MS = 90_000;
const HEARTBEAT_INTERVAL_MS = 5_000;
const RECONCILE_INTERVAL_MS = 2_000;

const REPLACED_MESSAGE =
  "Another client details view was opened. This view was closed.";

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore quota errors */
  }
}

function removeStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

function getTabId() {
  try {
    let tabId = sessionStorage.getItem(TAB_KEY);
    if (!tabId) {
      tabId = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      sessionStorage.setItem(TAB_KEY, tabId);
    }
    return tabId;
  } catch {
    return "fallback-tab";
  }
}

function readLock() {
  const clientId = readStorage(STORAGE_KEY);
  if (!clientId) return null;

  const heartbeat = Number(readStorage(HEARTBEAT_KEY) || 0);
  if (!heartbeat || Date.now() - heartbeat > STALE_MS) {
    clearOpenClientDetailsId();
    return null;
  }

  return {
    clientId: String(clientId),
    tabId: readStorage(TAB_KEY) || null,
    heartbeat,
  };
}

function isActiveClientDetailsView(pathname, userRole) {
  if (!isStaffUser(userRole)) return false;
  const routeClientId = extractClientDetailsIdFromPath(pathname);
  if (!routeClientId) return false;

  const lock = readLock();
  if (!lock) return false;

  return (
    lock.tabId === getTabId() &&
    lock.clientId === String(routeClientId)
  );
}

export function getOpenClientDetailsId() {
  return readLock()?.clientId || null;
}

/** Mark this tab as the sole active client-details view for the given client. */
export function setOpenClientDetailsId(clientId) {
  if (!clientId) return;
  writeStorage(STORAGE_KEY, String(clientId));
  writeStorage(TAB_KEY, getTabId());
  touchClientDetailsHeartbeat();
}

export function touchClientDetailsHeartbeat() {
  writeStorage(HEARTBEAT_KEY, String(Date.now()));
}

export function clearOpenClientDetailsId() {
  removeStorage(STORAGE_KEY);
  removeStorage(HEARTBEAT_KEY);
  removeStorage(TAB_KEY);
}

export function extractClientDetailsIdFromPath(path) {
  const match = String(path || "").match(/client_details\/([^/?#]+)/);
  return match?.[1] || null;
}

function isStaffUser(userRole) {
  return String(userRole || "").toLowerCase() !== "client";
}

function redirectStaleClientDetails(navigate, showToast) {
  if (showToast) {
    Toaster.info(REPLACED_MESSAGE);
  }
  navigate(getRolePath("/client"), { replace: true });
}

/**
 * Close this tab's client-details view if another tab/client is now active.
 */
export function reconcileClientDetailsRoute(
  navigate,
  pathname,
  userRole,
  { showToast = false } = {},
) {
  if (!isStaffUser(userRole)) return;

  const routeClientId = extractClientDetailsIdFromPath(pathname);
  if (!routeClientId) return;

  if (isActiveClientDetailsView(pathname, userRole)) {
    touchClientDetailsHeartbeat();
    return;
  }

  redirectStaleClientDetails(navigate, showToast);
}

/**
 * Register active client details for this tab and close stale views elsewhere.
 */
export function enforceClientDetailsRouteGuard(
  navigate,
  pathname,
  userRole,
  previousPathname = null,
) {
  if (!isStaffUser(userRole)) return;

  const clientId = extractClientDetailsIdFromPath(pathname);
  const previousClientId = extractClientDetailsIdFromPath(previousPathname);

  if (!clientId) {
    if (previousClientId) {
      const lock = readLock();
      if (lock?.tabId === getTabId()) {
        clearOpenClientDetailsId();
      }
    }
    return;
  }

  setOpenClientDetailsId(clientId);
  reconcileClientDetailsRoute(navigate, pathname, userRole);
}

export function getClientDetailsHeartbeatIntervalMs() {
  return HEARTBEAT_INTERVAL_MS;
}

export function getClientDetailsReconcileIntervalMs() {
  return RECONCILE_INTERVAL_MS;
}

export function isClientDetailsStorageEventKey(key) {
  return key === STORAGE_KEY || key === HEARTBEAT_KEY || key === TAB_KEY;
}

/**
 * Navigate to a client details page (replaces any previously open client).
 */
export function navigateToClientDetails(navigate, clientId, options = {}) {
  const { state, replace = false } = options;
  setOpenClientDetailsId(clientId);
  navigate(getRolePath(`/client_details/${clientId}`), { state, replace });
  return true;
}

/** Navigate when path may be client_details or another route. */
export function navigateWithClientDetailsGuard(navigate, path, options = {}) {
  const clientId = extractClientDetailsIdFromPath(path);
  if (clientId) {
    setOpenClientDetailsId(clientId);
  }
  navigate(path, options);
  return true;
}

/** Client list name click — no real href so new tabs cannot bypass the guard. */
export function openClientDetailsLink(navigate, clientId, options = {}) {
  return navigateToClientDetails(navigate, clientId, options);
}

/** Cross-tab sync when another tab opens client details. */
export function handleClientDetailsStorageSync(navigate, pathname, userRole) {
  reconcileClientDetailsRoute(navigate, pathname, userRole, { showToast: true });
}
